import axios from 'axios';
import { axiosInstance } from '../lib/axios';

export interface ChatbotConfig {
  businessId?: string;  // Optional because it's set by the server
  isEnabled: boolean;
  botName: string;
  welcomeMessage: string;
  primaryColor: string;
  secondaryColor: string;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  autoOpen: boolean;
  autoOpenDelay: number;
  collectUserInfo: boolean;
  requireEmail: boolean;
  systemPrompt: string;
  modelProvider: 'huggingface' | 'openai' | 'anthropic';
  modelName: string;
  apiKey?: string; // Only used for non-huggingface providers
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface HuggingFaceModelResponse {
  generated_text: string;
}

export type ApiResponse<T> = 
  | { success: true; data: T }
  | { success: false; error: string };

const handleApiError = (error: any): ApiResponse<any> => {
  if (axios.isAxiosError(error)) {
    // Don't throw on 401, let the interceptor handle it
    if (error.response?.status === 401) {
      return { success: false, error: 'Authentication required' };
    }
    return { success: false, error: error.response?.data?.message || error.message };
  }
  return { success: false, error: 'An unexpected error occurred' };
};

export const chatbotService = {
  // Get chatbot configuration
  async getConfig(): Promise<ApiResponse<ChatbotConfig>> {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: ChatbotConfig }>('/chatbot/config');
      
      // Set the default model provider to huggingface if not specified
      if (response.data.data && !response.data.data.modelProvider) {
        response.data.data.modelProvider = 'huggingface';
      }
      
      // Set default model name for Hugging Face if not specified
      if (response.data.data && response.data.data.modelProvider === 'huggingface' && !response.data.data.modelName) {
        response.data.data.modelName = 'mistralai/Mistral-7B-Instruct-v0.3';
      }
      
      return { success: true, data: response.data.data };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Update chatbot configuration
  async updateConfig(config: Partial<ChatbotConfig>): Promise<ApiResponse<ChatbotConfig>> {
    try {
      // For Hugging Face, we don't need to send the API key in the request
      // It's stored securely in environment variables
      const configToSend = { ...config };
      
      if (configToSend.modelProvider === 'huggingface') {
        delete configToSend.apiKey; // Remove API key for Hugging Face
      }
      
      const response = await axiosInstance.put<{ success: boolean; data: ChatbotConfig }>(
        '/chatbot/config', 
        configToSend
      );
      
      return { success: true, data: response.data.data };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get chat history
  async getChatHistory(sessionId: string): Promise<ApiResponse<ChatMessage[]>> {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: ChatMessage[] }>(`/chatbot/history?sessionId=${sessionId}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Send a message to Hugging Face API directly (for testing)
  async sendMessageToHuggingFace(
    message: string, 
    systemPrompt: string, 
    model: string = 'mistralai/Mistral-7B-Instruct-v0.3'
  ): Promise<ApiResponse<string>> {
    try {
      // This function uses the backend as a proxy to the Hugging Face API
      // This way, the API key is kept secure on the server
      const response = await axiosInstance.post<{ success: boolean; data: { response: string } }>(
        '/chatbot/message', 
        {
          message,
          systemPrompt,
          modelProvider: 'huggingface',
          modelName: model
        }
      );
      
      return { success: true, data: response.data.data.response };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get analytics
  async getAnalytics(startDate: Date, endDate: Date): Promise<ApiResponse<any>> {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: any }>('/chatbot/analytics', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      return { success: true, data: response.data.data };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get widget script
  async getWidgetScript(businessId: string): Promise<ApiResponse<string>> {
    try {
      const response = await axiosInstance.get<{ success: boolean; data: string }>(`/business/${businessId}/chatbot/widget.js`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return handleApiError(error);
    }
  }
}; 